'use client';

import { useMemo } from 'react';

import { useFeatureTogglesQuery } from '@query/featureToggles';

import type {
  ApplicationActivitiesPayload,
  ApplicationFarmPoolSchemaPayload,
  ApplicationHotOfferSchemaPayload,
} from '@api/types/applications';

import { CardsCarousel } from '@ui/uikit/components/common/CardsCarousel';
import { Tab, Tabs } from '@ui/uikit/components/common/Tabs';

import { FarmCardWithAPR } from '../../FarmCardWithAPR';
import { HotOfferCardWithAmountCalculations } from '../../HotOfferCardWithAmountCalculations';

interface ApplicationActivitiesProps {
  activities: ApplicationActivitiesPayload;
}

const ApplicationActivities = ({ activities }: ApplicationActivitiesProps) => {
  const { hotOffers, farmPools } = activities;
  const first20HotOffers = useMemo(() => hotOffers?.slice(0, 20) || [], [hotOffers]);
  const first20FarmPools = useMemo(() => farmPools?.slice(0, 20) || [], [farmPools]);

  const { data: { hotOffersSharing } = {} } = useFeatureTogglesQuery();

  const hasHotOffers = hotOffersSharing && first20HotOffers.length > 0;
  const hasFarmPools = first20FarmPools.length > 0;

  const defaultActiveTabId = hasHotOffers ? 'hot-offers' : hasFarmPools ? 'magic-farms' : undefined;

  return (
    <Tabs defaultActiveTabId={defaultActiveTabId}>
      <Tab label="Hot Offers" id="hot-offers" disabled={!hasHotOffers}>
        {hasHotOffers && (
          <CardsCarousel<ApplicationHotOfferSchemaPayload>
            items={first20HotOffers}
            render={(offer: ApplicationHotOfferSchemaPayload) => (
              <HotOfferCardWithAmountCalculations offer={offer} />
            )}
          />
        )}
      </Tab>

      <Tab label="Magic Farms" id="magic-farms" disabled={!hasFarmPools}>
        {hasFarmPools && (
          <CardsCarousel<ApplicationFarmPoolSchemaPayload>
            items={first20FarmPools}
            render={(pool: ApplicationFarmPoolSchemaPayload) => <FarmCardWithAPR pool={pool} />}
          />
        )}
      </Tab>
    </Tabs>
  );
};

export { ApplicationActivities };
